import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { userStore, bookStore } from '../../store';
import { hitApi } from '../../database/services/ApiHelper';
import { API } from '../../database/apis';
import {
  Search,
  X,
  SlidersHorizontal,
  BookOpen,
  Heart,
  HelpCircle,
  Leaf,
  User as UserIcon,
  Rocket,
  Shuffle,
  Bookmark,
  MoreVertical,
} from 'lucide-react';
import localforage from 'localforage';

// Discover screen — v2 design (2026-05-30).
//
// Three search modes via segmented tabs:
//   - Books   — list of book result rows (cover + title + author + rating)
//   - Authors — list of author rows (avatar + name + book count)
//   - Genres  — grid of genre tiles (name + book count + sample covers)
//
// Search-as-you-type with 300ms debounce. Recent-search chips show up
// when the query is empty (persisted across launches via localforage).
// Backend: /api/d2p/kleverbook/search?q=...&mode=...
//
// Falls back to client-side filter on userStore.adverBooks if the
// network request fails — the Discover screen should always render
// something usable.

type Mode = 'books' | 'authors' | 'genres';
type BookResult = {
  id: string;
  title: string;
  authorName: string;
  coverUrl?: string | null;
  averageRating?: number | null;
  reviewCount: number;
  category?: string;
  pageCount?: number | null;
};
type AuthorResult = {
  id: string;
  name: string;
  avatarUrl?: string | null;
  bookCount: number;
};
type GenreResult = {
  name: string;
  bookCount: number;
  coverSamples: string[];
};
type PopularWindow = 'week' | 'month' | 'all';

// Browse Popular chips — six pre-curated categories with brand colors,
// matching Mike's design. Tapping a chip seeds the search query so the
// user can drill in or just see what's there.
const BROWSE_CHIPS: { label: string; icon: any; bg: string; fg: string }[] = [
  { label: 'Fiction',   icon: BookOpen,   bg: '#EEF2FF', fg: '#4F46E5' },
  { label: 'Romance',   icon: Heart,      bg: '#FCE7F3', fg: '#DB2777' },
  { label: 'Mystery',   icon: HelpCircle, bg: '#DBEAFE', fg: '#2563EB' },
  { label: 'Self-Help', icon: Leaf,       bg: '#D1FAE5', fg: '#059669' },
  { label: 'Biography', icon: UserIcon,   bg: '#FED7AA', fg: '#EA580C' },
  { label: 'Sci-Fi',    icon: Rocket,     bg: '#EDE9FE', fg: '#7C3AED' },
];

const BRAND = '#5B47E0';
const BRAND_LIGHT = '#EDEAFC';
const TEXT_PRIMARY = '#0F172A';
const TEXT_MUTED = '#64748B';
const SURFACE = '#F1F5F9';
const STAR_YELLOW = '#FFB800';

const RECENT_KEY = 'discover:recentSearches';
const MAX_RECENT = 6;
const DEBOUNCE_MS = 300;

const DiscoverScreen: React.FC = observer(() => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const focusOnMount = params.get('focus') === '1';
  const categoryFromQuery = params.get('category') || '';

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState(categoryFromQuery);
  const [debouncedQuery, setDebouncedQuery] = useState(categoryFromQuery);
  const [mode, setMode] = useState<Mode>('books');
  const [loading, setLoading] = useState(false);

  const [books, setBooks] = useState<BookResult[]>([]);
  const [authors, setAuthors] = useState<AuthorResult[]>([]);
  const [genres, setGenres] = useState<GenreResult[]>([]);
  const [recent, setRecent] = useState<string[]>([]);

  // Popular landing — independent state from search results.
  const [popular, setPopular] = useState<BookResult[]>([]);
  const [popularLoading, setPopularLoading] = useState(false);
  const [popularWindow, setPopularWindow] = useState<PopularWindow>('week');

  // Auto-focus when launched from Home's search pill.
  useEffect(() => {
    if (focusOnMount && inputRef.current) inputRef.current.focus();
    loadRecent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce the typed query before firing requests so we don't hammer
  // the backend on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  // Fetch whenever the debounced query OR the mode changes.
  useEffect(() => {
    void fetchResults(debouncedQuery, mode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, mode]);

  async function fetchResults(q: string, m: Mode) {
    setLoading(true);
    try {
      const token = userStore.authToken || userStore.deviceToken;
      const res: any = await hitApi({
        endpoint: `${API.SEARCH}?q=${encodeURIComponent(q)}&mode=${m}&limit=20`,
        method: 'GET',
        token,
      });
      if (m === 'books') setBooks(Array.isArray(res?.books) ? res.books : []);
      else if (m === 'authors') setAuthors(Array.isArray(res?.authors) ? res.authors : []);
      else if (m === 'genres') setGenres(Array.isArray(res?.genres) ? res.genres : []);
    } catch {
      // Network failure — fall back to a client-side filter on already-
      // loaded books so the screen never goes blank.
      if (m === 'books') {
        setBooks(clientSideFallback(q));
      } else {
        if (m === 'authors') setAuthors([]);
        if (m === 'genres') setGenres([]);
      }
    } finally {
      setLoading(false);
    }
  }

  // Re-fetch the Popular row whenever the time window changes. Empty
  // catalog days fall back to "all time" inside the route itself, so
  // this never returns nothing when the catalog has any reviewed books.
  useEffect(() => {
    void fetchPopular(popularWindow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popularWindow]);

  async function fetchPopular(win: PopularWindow) {
    setPopularLoading(true);
    try {
      const token = userStore.authToken || userStore.deviceToken;
      const res: any = await hitApi({
        endpoint: `${API.POPULAR}?window=${win}&limit=10`,
        method: 'GET',
        token,
      });
      setPopular(Array.isArray(res?.books) ? res.books : []);
    } catch {
      // Quiet failure — the landing falls back to whatever Popular
      // happened to be cached locally.
    } finally {
      setPopularLoading(false);
    }
  }

  function clientSideFallback(q: string): BookResult[] {
    const lower = q.toLowerCase();
    return userStore.adverBooks
      .filter((b: any) =>
        !lower ||
        b.title?.toLowerCase().includes(lower) ||
        b.authorName?.toLowerCase().includes(lower),
      )
      .slice(0, 20)
      .map((b: any) => ({
        id: b._id,
        title: b.title,
        authorName: b.authorName || 'Unknown',
        coverUrl: b.coverImage || null,
        averageRating: null,
        reviewCount: 0,
        category: typeof b.category === 'object' ? b.category?.name : b.category,
      }));
  }

  async function loadRecent() {
    try {
      const raw = await localforage.getItem<string>(RECENT_KEY);
      if (raw) setRecent(JSON.parse(raw));
    } catch {}
  }

  async function commitRecent(q: string) {
    if (!q.trim()) return;
    const next = [q.trim(), ...recent.filter((r) => r !== q.trim())].slice(0, MAX_RECENT);
    setRecent(next);
    try {
      await localforage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch {}
  }

  function handleSubmit() {
    if (query.trim()) commitRecent(query);
  }

  function openBook(bookId: string) {
    // The result rows don't carry the full Book observable — only the id +
    // display fields. Find the matching observable in adverBooks (if it's
    // loaded) so the Book screen still gets the same shape; otherwise
    // synthesize a minimal Book stub from the result row.
    const full = userStore.adverBooks.find((b: any) => b._id === bookId);
    if (full) {
      bookStore.setSelectedBook(toJS(full));
    } else {
      const row = books.find((b) => b.id === bookId);
      if (row) {
        bookStore.setSelectedBook({
          _id: row.id,
          title: row.title,
          authorName: row.authorName,
          category: row.category || '',
          coverImage: row.coverUrl || '',
          description: '',
          ad_slots: [],
        } as any);
      }
    }
    navigate('/book');
  }

  const showEmptyState = !loading && (
    (mode === 'books' && books.length === 0) ||
    (mode === 'authors' && authors.length === 0) ||
    (mode === 'genres' && genres.length === 0)
  );

  return (
    <div className="h-full flex flex-col" style={{ background: '#FFFFFF' }}>
      {/* === Top bar === */}
      <div className="safe-area-top" style={{ background: '#FFFFFF' }}>
        <div className="px-5 pt-4 pb-3">
          <h1
            className="text-[28px] font-bold tracking-tight mb-3"
            style={{ color: TEXT_PRIMARY, lineHeight: 1.1 }}
          >
            Discover
          </h1>

          {/* Search input */}
          <div
            className="flex items-center gap-3 h-12 px-4 rounded-2xl"
            style={{ background: SURFACE }}
          >
            <Search className="w-[18px] h-[18px] flex-shrink-0" style={{ color: TEXT_MUTED }} strokeWidth={2} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              placeholder="Search books, authors, genres..."
              className="flex-1 bg-transparent outline-none text-[15px]"
              style={{ color: TEXT_PRIMARY }}
              autoFocus={focusOnMount}
            />
            {query ? (
              <button
                onClick={() => {
                  setQuery('');
                  inputRef.current?.focus();
                }}
                aria-label="Clear search"
                className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors"
                style={{ background: '#CBD5E1' }}
              >
                <X className="w-[12px] h-[12px] text-white" strokeWidth={3} />
              </button>
            ) : (
              // Filter icon stub — the visual matches the design. When we
              // ship real filters (price, length, language) wire this up;
              // for now it's a non-functional decoration that signals
              // "search has filters."
              <SlidersHorizontal
                className="w-[18px] h-[18px] flex-shrink-0"
                style={{ color: TEXT_MUTED }}
                strokeWidth={2}
              />
            )}
          </div>
        </div>

        {/* Mode tabs — only shown when there's an active query */}
        {debouncedQuery && (
          <div className="px-5 pb-3 flex items-center gap-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            <ModeTab label="Books"   active={mode === 'books'}   onClick={() => setMode('books')} />
            <ModeTab label="Authors" active={mode === 'authors'} onClick={() => setMode('authors')} />
            <ModeTab label="Genres"  active={mode === 'genres'}  onClick={() => setMode('genres')} />
          </div>
        )}
      </div>

      {/* === Body === */}
      <div className="flex-1 overflow-y-auto">
        {/* === IDLE: the new Discover landing === */}
        {!debouncedQuery && (
          <DiscoverLanding
            popular={popular}
            popularLoading={popularLoading}
            popularWindow={popularWindow}
            onWindowChange={setPopularWindow}
            onChipPick={(label) => setQuery(label)}
            onSearchHeroClick={() => inputRef.current?.focus()}
            onSurpriseMe={() => {
              if (popular.length === 0) return;
              const random = popular[Math.floor(Math.random() * popular.length)];
              openBook(random.id);
            }}
            onOpenBook={openBook}
            onViewAllCategories={() => navigate('/app/browse')}
            onViewAllPopular={(w) => navigate(`/app/popular?window=${w}`)}
          />
        )}

        {/* === TYPING: the search results === */}
        {debouncedQuery && (
          <div className="px-5 pb-6">
            {loading && books.length === 0 && authors.length === 0 && genres.length === 0 ? (
              <SkeletonList />
            ) : showEmptyState ? (
              <ResultsEmpty query={debouncedQuery} />
            ) : (
              <>
                {mode === 'books' &&
                  books.map((b) => <BookRow key={b.id} book={b} onClick={() => openBook(b.id)} />)}
                {mode === 'authors' &&
                  authors.map((a) => <AuthorRow key={a.id} author={a} />)}
                {mode === 'genres' && (
                  <div className="grid grid-cols-2 gap-3">
                    {genres.map((g) => <GenreTile key={g.name} genre={g} onClick={() => setQuery(g.name)} />)}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

/* ====================================================================
 *  Subcomponents
 * ==================================================================== */

function ModeTab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex-shrink-0 h-9 px-5 rounded-full text-[14px] font-semibold transition-colors"
      style={{
        background: active ? BRAND : '#FFFFFF',
        color: active ? '#FFFFFF' : TEXT_PRIMARY,
        border: active ? 'none' : '1px solid #E2E8F0',
      }}
    >
      {label}
    </button>
  );
}

function BookRow({ book, onClick }: { book: BookResult; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 py-3 text-left transition-colors active:bg-[#F8FAFC]"
      style={{ borderBottom: '1px solid #F1F5F9' }}
    >
      <div
        className="w-14 h-[72px] rounded-xl flex-shrink-0 overflow-hidden"
        style={{ background: '#E2E8F0' }}
      >
        {book.coverUrl && (
          <img src={book.coverUrl} alt={book.title} className="w-full h-full object-cover" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[15px] font-semibold leading-tight truncate" style={{ color: TEXT_PRIMARY }}>
          {book.title}
        </p>
        <p className="text-[13px] mt-0.5 truncate" style={{ color: TEXT_MUTED }}>
          {book.authorName}
        </p>
        {typeof book.averageRating === 'number' && book.averageRating > 0 && (
          <div className="flex items-center gap-1.5 mt-1">
            <span className="text-[12px]" style={{ color: STAR_YELLOW }}>★</span>
            <span className="text-[12px] font-semibold" style={{ color: TEXT_PRIMARY }}>
              {book.averageRating.toFixed(1)}
            </span>
            {book.reviewCount > 0 && (
              <span className="text-[12px]" style={{ color: TEXT_MUTED }}>
                ({formatCount(book.reviewCount)})
              </span>
            )}
          </div>
        )}
      </div>
    </button>
  );
}

function AuthorRow({ author }: { author: AuthorResult }) {
  return (
    <div
      className="w-full flex items-center gap-3 py-3"
      style={{ borderBottom: '1px solid #F1F5F9' }}
    >
      <div
        className="w-12 h-12 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center text-[16px] font-bold"
        style={{ background: BRAND_LIGHT, color: BRAND }}
      >
        {author.avatarUrl
          ? <img src={author.avatarUrl} alt={author.name} className="w-full h-full object-cover" />
          : author.name.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[15px] font-semibold leading-tight truncate" style={{ color: TEXT_PRIMARY }}>
          {author.name}
        </p>
        <p className="text-[13px] mt-0.5" style={{ color: TEXT_MUTED }}>
          {author.bookCount} book{author.bookCount === 1 ? '' : 's'}
        </p>
      </div>
    </div>
  );
}

function GenreTile({ genre, onClick }: { genre: GenreResult; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="rounded-2xl p-4 text-left transition-transform active:scale-[0.98]"
      style={{ background: BRAND_LIGHT }}
    >
      <p className="text-[16px] font-bold mb-3" style={{ color: TEXT_PRIMARY }}>{genre.name}</p>
      <div className="flex gap-1 mb-3">
        {genre.coverSamples.slice(0, 3).map((c, i) => (
          <div
            key={i}
            className="w-8 h-10 rounded-md overflow-hidden flex-shrink-0"
            style={{ background: '#FFFFFF' }}
          >
            {c && <img src={c} alt="" className="w-full h-full object-cover" />}
          </div>
        ))}
      </div>
      <p className="text-[12px] font-medium" style={{ color: TEXT_MUTED }}>
        {genre.bookCount} book{genre.bookCount === 1 ? '' : 's'}
      </p>
    </button>
  );
}

/* ====================================================================
 *  Empty + idle states
 * ==================================================================== */

function ResultsEmpty({ query }: { query: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <img
        src={`${process.env.PUBLIC_URL || ''}/assets/no_search_found.png`}
        alt=""
        className="w-32 h-32 mb-4 opacity-90"
      />
      <p className="text-[16px] font-semibold" style={{ color: TEXT_PRIMARY }}>No results found</p>
      <p className="text-[13px] mt-1" style={{ color: TEXT_MUTED }}>
        We couldn&apos;t find anything matching &quot;{query}&quot;
      </p>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="pt-2">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="flex items-center gap-3 py-3"
          style={{ borderBottom: '1px solid #F1F5F9' }}
        >
          <div className="w-14 h-[72px] rounded-xl flex-shrink-0" style={{ background: SURFACE }} />
          <div className="flex-1">
            <div className="h-3 rounded w-3/5 mb-2" style={{ background: SURFACE }} />
            <div className="h-3 rounded w-2/5" style={{ background: SURFACE }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ====================================================================
 *  DiscoverLanding — the idle-state hero + sections layout
 *
 *  Per Mike's design 2026-05-30 evening:
 *    1. "Find your next great read" hero card with magnifier illustration
 *    2. "Browse Popular" — six colored chip buttons (Fiction, Romance, ...)
 *    3. "Popular this week" — horizontal carousel of ranked books,
 *       with a Week / Month / All toggle
 *    4. "Can't decide what to read? Surprise me" CTA banner
 * ==================================================================== */

interface DiscoverLandingProps {
  popular: BookResult[];
  popularLoading: boolean;
  popularWindow: PopularWindow;
  onWindowChange: (w: PopularWindow) => void;
  onChipPick: (label: string) => void;
  onSearchHeroClick: () => void;
  onSurpriseMe: () => void;
  onOpenBook: (id: string) => void;
  onViewAllCategories: () => void;
  onViewAllPopular: (window: PopularWindow) => void;
}

function DiscoverLanding({
  popular,
  popularLoading,
  popularWindow,
  onWindowChange,
  onChipPick,
  onSearchHeroClick,
  onSurpriseMe,
  onOpenBook,
  onViewAllCategories,
  onViewAllPopular,
}: DiscoverLandingProps) {
  return (
    <div className="pb-6">
      {/* === 1. Hero === */}
      <div className="px-5 pt-1">
        <div
          className="relative rounded-3xl p-6 overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #EEEAFC 0%, #F4EFFE 60%, #FAE9FB 100%)',
          }}
        >
          <div className="relative z-10 max-w-[210px]">
            <h2 className="text-[22px] font-bold leading-tight" style={{ color: TEXT_PRIMARY }}>
              Find your next
            </h2>
            <h2 className="text-[22px] font-bold leading-tight" style={{ color: BRAND }}>
              great read
            </h2>
            <p className="text-[13px] mt-2.5 leading-snug" style={{ color: TEXT_MUTED }}>
              Search millions of books by title, author or keyword.
            </p>
            <button
              onClick={onSearchHeroClick}
              className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-xl text-[13px] font-semibold text-white"
              style={{
                background: BRAND,
                boxShadow: '0 12px 24px -8px rgba(91,71,224,0.5)',
              }}
            >
              Search books
              <Search className="w-[14px] h-[14px]" strokeWidth={2.4} />
            </button>
          </div>

          {/* Magnifier illustration on the right (CSS only — keeps the
              bundle lean and matches the warm purple palette). */}
          <MagnifierGlyph />
        </div>
      </div>

      {/* === 2. Browse Popular === */}
      <section className="px-5 mt-8">
        <SectionHeader title="Browse Popular" actionLabel="View all" onAction={onViewAllCategories} />
        <div className="grid grid-cols-2 gap-3 mt-3">
          {BROWSE_CHIPS.map((c) => (
            <button
              key={c.label}
              onClick={() => onChipPick(c.label)}
              className="flex items-center gap-3 h-12 px-3.5 rounded-2xl transition-transform active:scale-[0.98]"
              style={{ background: '#FFFFFF', border: '1px solid #F1F5F9' }}
            >
              <span
                className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: c.bg }}
              >
                <c.icon className="w-[16px] h-[16px]" style={{ color: c.fg }} strokeWidth={2.2} />
              </span>
              <span className="text-[14px] font-semibold" style={{ color: TEXT_PRIMARY }}>
                {c.label}
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* === 3. Popular this week + window toggle === */}
      <section className="mt-8">
        <div className="px-5">
          <SectionHeader
            title={popularWindow === 'week' ? 'Popular this week' : popularWindow === 'month' ? 'Popular this month' : 'Popular of all time'}
            actionLabel="See all"
            onAction={() => onViewAllPopular(popularWindow)}
          />
          {/* Window toggle */}
          <div className="flex items-center gap-2 mt-3">
            {(['week', 'month', 'all'] as PopularWindow[]).map((w) => (
              <button
                key={w}
                onClick={() => onWindowChange(w)}
                className="h-8 px-3.5 rounded-full text-[12px] font-semibold transition-colors"
                style={{
                  background: popularWindow === w ? BRAND : '#FFFFFF',
                  color: popularWindow === w ? '#FFFFFF' : TEXT_MUTED,
                  border: popularWindow === w ? 'none' : '1px solid #E2E8F0',
                }}
              >
                {w === 'week' ? 'Week' : w === 'month' ? 'Month' : 'All time'}
              </button>
            ))}
          </div>
        </div>

        {/* Carousel */}
        <div
          className="flex gap-3 overflow-x-auto px-5 mt-4 pb-2"
          style={{ scrollbarWidth: 'none' }}
        >
          {popularLoading && popular.length === 0
            ? [0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="w-[140px] flex-shrink-0"
                >
                  <div
                    className="w-[140px] h-[200px] rounded-2xl"
                    style={{ background: SURFACE }}
                  />
                  <div className="h-3 mt-3 rounded w-3/4" style={{ background: SURFACE }} />
                  <div className="h-3 mt-1.5 rounded w-1/2" style={{ background: SURFACE }} />
                </div>
              ))
            : popular.map((b) => (
                <PopularCard key={b.id} book={b} onClick={() => onOpenBook(b.id)} />
              ))}
        </div>
      </section>

      {/* === 4. Surprise me === */}
      <section className="px-5 mt-6">
        <div
          className="flex items-center gap-3 rounded-2xl p-4"
          style={{ background: '#F8F5FE', border: '1px solid #E2DFF5' }}
        >
          <span
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: BRAND_LIGHT }}
          >
            <Bookmark className="w-[18px] h-[18px]" style={{ color: BRAND }} strokeWidth={2.2} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[14px] font-semibold leading-snug" style={{ color: TEXT_PRIMARY }}>
              Can&apos;t decide what to read?
            </p>
            <p className="text-[12.5px] mt-0.5 leading-snug" style={{ color: TEXT_MUTED }}>
              Let us help you find your next favorite book
            </p>
          </div>
          <button
            onClick={onSurpriseMe}
            className="flex items-center gap-1.5 h-9 px-3.5 rounded-xl text-[13px] font-semibold whitespace-nowrap"
            style={{
              background: '#FFFFFF',
              color: BRAND,
              border: '1px solid #E2DFF5',
            }}
          >
            <Shuffle className="w-[14px] h-[14px]" strokeWidth={2.4} />
            Surprise me
          </button>
        </div>
      </section>
    </div>
  );
}

function SectionHeader({
  title,
  actionLabel,
  onAction,
}: {
  title: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <h2 className="text-[18px] font-bold tracking-tight" style={{ color: TEXT_PRIMARY }}>
        {title}
      </h2>
      {actionLabel && (
        <button
          onClick={onAction}
          className="text-[13px] font-semibold"
          style={{ color: BRAND }}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

function PopularCard({ book, onClick }: { book: BookResult; onClick: () => void }) {
  return (
    <div className="w-[140px] flex-shrink-0">
      <button
        onClick={onClick}
        className="w-[140px] text-left"
      >
        <div
          className="w-[140px] h-[200px] rounded-2xl overflow-hidden mb-2.5"
          style={{
            background: '#E2E8F0',
            boxShadow: '0 12px 28px -10px rgba(15, 23, 42, 0.2)',
          }}
        >
          {book.coverUrl && (
            <img src={book.coverUrl} alt={book.title} className="w-full h-full object-cover" />
          )}
        </div>
        <p className="text-[13.5px] font-semibold leading-tight truncate" style={{ color: TEXT_PRIMARY }}>
          {book.title}
        </p>
        <p className="text-[12px] mt-0.5 truncate" style={{ color: TEXT_MUTED }}>
          {book.authorName}
        </p>
        {typeof book.averageRating === 'number' && book.averageRating > 0 && (
          <div className="flex items-center gap-1 mt-1">
            <span className="text-[11px]" style={{ color: STAR_YELLOW }}>★</span>
            <span className="text-[11px] font-semibold" style={{ color: TEXT_PRIMARY }}>
              {book.averageRating.toFixed(1)}
            </span>
            {book.reviewCount > 0 && (
              <span className="text-[11px]" style={{ color: TEXT_MUTED }}>
                ({formatCount(book.reviewCount)})
              </span>
            )}
          </div>
        )}
      </button>
      {/* Quick-actions menu placeholder — keeps the design's three-dot
          affordance without committing to a context-menu implementation
          yet (that lands with PR 3 Library actions). */}
      <button
        onClick={(e) => e.stopPropagation()}
        aria-label="More options"
        className="mt-1 ml-auto block opacity-40"
      >
        <MoreVertical className="w-[14px] h-[14px]" style={{ color: TEXT_MUTED }} />
      </button>
    </div>
  );
}

function MagnifierGlyph() {
  // Pure-CSS / SVG magnifier-and-books illustration. Sits to the right
  // of the hero copy. Drawn rather than sourced from /assets because
  // none of the empty-state PNGs match this hero motif exactly.
  return (
    <div
      className="absolute right-3 bottom-3 pointer-events-none"
      aria-hidden="true"
    >
      <svg width="110" height="110" viewBox="0 0 120 120">
        {/* Book stack */}
        <rect x="55" y="78" width="44" height="10" rx="3" fill="#F472B6" />
        <rect x="60" y="68" width="38" height="10" rx="3" fill="#A78BFA" />
        <rect x="64" y="58" width="32" height="10" rx="3" fill="#C4B5FD" />
        {/* Magnifier */}
        <circle cx="44" cy="52" r="26" fill="rgba(255,255,255,0.6)" stroke="#7C3AED" strokeWidth="5" />
        <circle cx="44" cy="52" r="20" fill="rgba(255,255,255,0.55)" />
        <line x1="64" y1="72" x2="78" y2="86" stroke="#7C3AED" strokeWidth="6" strokeLinecap="round" />
      </svg>
    </div>
  );
}

/* ====================================================================
 *  Utilities
 * ==================================================================== */

function formatCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10_000) return `${(n / 1000).toFixed(1)}K`;
  if (n < 1_000_000) return `${Math.round(n / 1000)}K`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

export default DiscoverScreen;
